<template>
  <notice-bar v-if="showNoticeBar" />
  <header class="header-area" :class="rootClasses" ref="header">
    <div class="header-area__main-menu-background" />
    <button 
      v-if="!showCartHeader" 
      class="header-area__mobile-menu-toggle" 
      @click="toggleMenu"
      @mouseenter.prevent
      @mouseleave.prevent>
      <icon-menu class="header-area__mobile-toggle-icon" />
    </button>
    <nuxt-link class="header-area__menu-logo" to="/" @click.prevent="clearQuery">
      <img src="/assets/images/asoundeffect-logo.svg" height="40px"></img>
    </nuxt-link>
    <div v-if="showCartHeader" class="header-area__cart-page">
      {{ route.name === 'checkout' ? 'Checkout' : 'Cart' }}
    </div>
    <div class="header-area__right-menu-container">
      <div class="header-area__mobile-menu-wrapper" ref="mobile-menu-wrapper">
        <div v-if="!showCartHeader" class="header-area__mobile-menu">
          <main-menu
            v-if="menu"
            class="header-area__main-menu"
            :menu-nodes="menu"
            @linkClicked="closeMenu" />
        </div>
        <div v-if="! isLoading" class="header-area__user-container header-area__user-container--mobile">
          <div v-if="isLoggedIn" class="header-area__user-logged-in">
            <nuxt-link class="header-area__item" to="/my-account/">
              My Account<span v-if="currentUser?.username">&nbsp;|&nbsp;{{ currentUser.username }}</span>
            </nuxt-link>
          </div>
          <nuxt-link v-else class="header-area__item" to="/login/">
            Sign In
          </nuxt-link>
        </div>
      </div>
      <div class="header-area__right_menu">
        <search-bar
            v-if="!showCartHeader"
            class="header-area__search-bar"
            bar-size="compact"
            item-position="header" />
        <div v-if="! isLoading" class="header-area__user-container header-area__user-container--desktop">
          <div v-if="isLoggedIn" class="header-area__user-logged-in">
            <nuxt-link class="header-area__item" to="/my-account/">
              My Account<span v-if="currentUser?.username">&nbsp;|&nbsp;{{ currentUser.username }}</span>
            </nuxt-link>
          </div>
          <nuxt-link v-else class="header-area__item" to="/login/">
            Sign In
          </nuxt-link>
        </div>
        <template v-if="isLoggedIn">
          <nuxt-link
            to="/my-account/favorites"
            class="header-area__favorites"
            title="Wishlist"
          >
            <IconHeartFilled v-if="hasWishlistItems" class="header-area__favorites-icon" />
            <IconHeart v-else class="header-area__favorites-icon" />
          </nuxt-link>
        </template>
        <!-- Hidden, not removed, on checkout: the billing country drives tax and currency there,
             and this component's watcher is what sends the billing form's country switch to the server. -->
        <country-selector
          v-show="route.name !== 'checkout'"
          class="header-area__item"
          :country-options="countries" />
        <button
          v-show="showCountryTaxOptions && selectedTaxOptionValue"
          class="header-area__open-tax-selector"
          id="open-tax-selector"
          @click="openTaxSelector = ! openTaxSelector"
        >
          {{ selectedTaxOptionValue }}
        </button>
        <div v-show="openTaxSelector" class="tax-selector-dropdown" ref="taxSelectorRef">
          <b>Price Display</b>
          <show-tax-selector emit-updates @tax-selected="selectedTaxOption"/>
        </div>
        <cart-selector
          v-if="!showCartHeader"
          class="right_menu__item" />
      </div>
    </div>
  </header>
  <mini-cart v-if="isMiniCartOpen"/>
</template>

<script setup lang="ts">
import { useAuthStore } from '~/stores/auth';
import { useUserStore } from '~/stores/user';
import { useCartStore } from '~/stores/cart';
import { useProductStore } from '~/stores/product';
import { useAudioPlayerStore } from '~/stores/audioplayer.js';
import useDetectOutsideClick from "~/composables/detectOutsideClick";
import {
  computed,
  defineAsyncComponent,
  nextTick,
  ref,
  useTemplateRef,
  watch,
  onMounted,
  useFetch,
  useHead,
  useRoute,
  useRuntimeConfig,
  inject
} from '#imports';

const authStore = useAuthStore();
const userStore = useUserStore();
const cartStore = useCartStore();
const productStore = useProductStore();
const audioPlayerStore = useAudioPlayerStore();
const route = useRoute();
const header = useTemplateRef('header');
const mobileMenuWrapper = useTemplateRef('mobile-menu-wrapper');
const MainMenu = defineAsyncComponent(() => import('~/components/parts/menu/MainMenu.vue'));
const SearchBar = defineAsyncComponent(() => import('~/components/SearchBar.vue'));
const CountrySelector = defineAsyncComponent(() => import('~/components/CountrySelector.vue'));
const NoticeBar = defineAsyncComponent(() => import('~/components/NoticeBar.vue'));
const CartSelector = defineAsyncComponent(() => import('~/components/CartSelector.vue'));
const MiniCart = defineAsyncComponent(() => import('~/components/MiniCart.vue'));
const IconMenu = defineAsyncComponent(() => import('~/assets/icons/IconMenu.svg'));
const IconPower = defineAsyncComponent(() => import('~/assets/icons/IconPower.svg'));
const IconHeart = defineAsyncComponent(() => import('~/assets/icons/IconHeart.svg'));
const IconHeartFilled = defineAsyncComponent(() => import('~/assets/icons/IconHeartFilled.svg'));
const menu = ref([]);
const countries = ref([]);
const showNoticeBar = true;
const mobileMenuOpen = ref(false);
const hasWishlistItems = computed(() => {
  const hasItems = authStore.isAuthenticated &&
      productStore.wishlists?.length > 0 &&
      productStore.wishlists.some(wishlist => wishlist.count > 0);

  return hasItems;
});

watch(route, () => {
  if (mobileMenuOpen.value) {
    closeMenu();
  }
});

const isLoading = ref(false)

const openTaxSelector = ref(false)
const taxSelectorRef = ref()
useDetectOutsideClick('open-tax-selector', taxSelectorRef, () => {
  openTaxSelector.value = false
})

onMounted(() => {
  isLoading.value = true
})

const authLoading = computed(() => {
  return authStore.loading
})

watch(authLoading, (updated) => isLoading.value = !!updated)

const isLoggedIn = computed(() => {
  return authStore.isAuthenticated;
});
const currentUser = computed(() => {
  return userStore.getCurrentUser;
});
const isMiniCartOpen = computed(() => {
  return cartStore.isMiniCartOpen;
});
const showCartHeader = computed(() => {
  return ['/cart', '/checkout'].includes(route.path);
});
const rootClasses = computed(() => {
  return {
    'header-area--cart': showCartHeader.value,
    'header-area--menu-open': mobileMenuOpen.value,
  };
});
const showCountryTaxOptions = computed(() => {
  return userStore.getCountryHasTax && window.innerWidth > 1024;
})

const selectedTaxOptionValue = ref(userStore.getShowProductTax ? 'Incl. VAT' : 'Excl. VAT');
function selectedTaxOption(value) {
  if (showCountryTaxOptions) {
    selectedTaxOptionValue.value = value
  }
}

async function toggleMenu () {
  if (mobileMenuOpen.value) {
    await closeMenu();
  } else {
    mobileMenuOpen.value = true;
    await nextTick();

    // Calculate max-height accounting for notice bar
    const noticeBar = document.querySelector('.notice_bar');
    const noticeBarHeight = noticeBar ? noticeBar.offsetHeight : 0;
    const headerHeight = header.value ? header.value.offsetHeight : 0;
    const availableHeight = window.innerHeight - noticeBarHeight - headerHeight;

    // Set max-height on wrapper
    if (mobileMenuWrapper.value) {
      mobileMenuWrapper.value.style.maxHeight = `${availableHeight}px`;
    }

    // Prevent body scroll behind menu using simple CSS
    document.body.style.overflow = 'hidden';
    // The menu wrapper itself will scroll via CSS (max-height + overflow: auto)
  }
}

async function closeMenu () {
  document.body.style.overflow = '';
  if (mobileMenuWrapper.value) {
    mobileMenuWrapper.value.style.maxHeight = '';
  }
  mobileMenuOpen.value = false;
  await nextTick();
}

const clearQuery = inject('clearQuery');

async function logout () {
  await authStore.logoutUser();
  location.replace('/login/');
}

const config = useRuntimeConfig();
const { data } = await useFetch(`${config.public.baseUrl}/wp-json/content-search/v1/search?type=menu`, {
  pick: ['menu', 'countries', 'globalStylesheet', 'audioPlayerToken'],
});

if (data.value) {
  useHead({
    link: [{
      href: data.value.globalStylesheet,
      rel: 'stylesheet',
    }],
  });

  menu.value = data.value.menu;
  countries.value = data.value.countries;
}

onMounted(async () => {
  if (authStore.isAuthenticated) {
    await productStore.getWishlists();
  }
});

watch(() => authStore.isAuthenticated, async (isAuthenticated) => {
  if (isAuthenticated) {
    await productStore.getWishlists();
  }
});
</script>

<style lang="scss">
@use "~/common/scss/variables";
@use "~/common/scss/container";
@use "~/common/scss/helpers";
@use "~/common/scss/mq";

:root {
  --menu-height: #{helpers.px-to-rem(60)};
}

.header-area {
  $root: &;

  align-items: stretch;
  background: #fff;
  border-bottom: solid 1px variables.$colorGrey;
  display: grid;
  font-family: var(--header-font-family);
  font-size: helpers.px-to-rem(12);
  gap: helpers.px-to-rem(30);
  grid-template: "mobileToggle logo actions" auto / 1fr auto 1fr;
  height: var(--menu-height);
  padding: 0 helpers.px-to-rem(25);
  position: sticky;
  top: 0;
  z-index: 40;

  @include mq.mq(variables.$mq-4-max) {
    &--menu-open {
      max-width: 100vw;
    }
  }

  @include mq.mq(variables.$mq-5-min) {
    grid-template: "logo actions" auto / auto 1fr;
  }

  @include mq.mq(variables.$mq-3-min, variables.$mq-4-max) {
    .header-area__right-menu-container {
      justify-content: flex-end;
    }

    .header-area__mobile-menu-wrapper {
      justify-content: flex-end;
    }

    .header-area__user-container {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;

      a {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
  @include mq.mobileOnly {
    &__favorites-icon {
      margin: .2rem 0.7rem 0 .1rem;
    }

    &__mobile-toggle-icon {
      margin: .5rem 0 0 -.9rem
    }
  }

  &--cart {
    grid-template: "logo name actions" auto / 1fr auto 1fr;
  }

  &__mobile-menu-toggle {
    color: #000;
    flex: 0 0 1rem;
    grid-area: mobileToggle;
    width: 1rem;

    #{$root}--cart & {
      display: none;
    }

    @include mq.mq(variables.$mq-5-min) {
      display: none;
    }
  }

  &__open-tax-selector {
    border: solid 1px;
    padding: 0.2rem;
    background: #ddd;
    text-wrap-mode: nowrap;
  }

  .tax-selector-dropdown {
    position: absolute;
    top: 60px;
    z-index: 999;
    background-color: #fff;
    padding: 1.3rem 1.7rem;
    border: solid 2px;
    border-radius: 3px;
  }

  &__right-menu-container {
    align-items: stretch;
    display: flex;
    gap: 1rem;
    grid-area: actions;

    #{$root}--cart & {
      justify-content: flex-end;
    }

    @include mq.mq(variables.$mq-4-max) {
      justify-content: flex-end;
    }
  }

  &__mobile-menu-wrapper {
    display: flex;
    width: 80%;
    gap: 1rem;

    #{$root}--cart & {
      width: auto;
    }

    @include mq.mq(variables.$mq-4-max) {
      justify-content: flex-end;
    }

    @include mq.mq(variables.$mq-4-max) {
      display: none;

      #{$root}--menu-open & {
        display: flex;
        flex-flow: column;
        align-items: center;
        left: 0;
        max-height: calc(100vh - var(--menu-height));
        overflow: auto;
        position: absolute;
        top: var(--menu-height);
        width: 100%;
      }
    }

    @include mq.mq(variables.$mq-5-min) {
      display: flex;
    }
  }

  &__mobile-menu {
    @include mq.mq(variables.$mq-4-max) {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      flex-shrink: 0; // Prevent shrinking so wrapper can scroll
    }

    @include mq.mq(variables.$mq-5-min) {
      display: flex;
      flex-direction: row;
      gap: 4rem;
      width: 100%;
    }
  }

  &__main-menu.main-menu {
    @include mq.mq(variables.$mq-4-max) {
      display: none;
      margin-bottom: 1.5rem;
      width: 100%;

      #{$root}--menu-open & {
        display: flex;
        flex-flow: column;
      }
    }

    @include mq.mq(variables.$mq-5-min) {
      display: flex;
    }
  }

  &__search-bar {
    align-self: center;
  }

  &__user-container {
    display: flex;

    a {
      white-space: nowrap;
    }

    &--desktop {
      display: none;

      @include mq.mq(variables.$mq-5-min) {
        display: flex;
      }
    }

    &--mobile {
      display: flex;

      @include mq.mq(variables.$mq-5-min) {
        display: none;
      }

      @include mq.mq(variables.$mq-4-max) {
        margin-bottom: 2rem;
        text-align: center;
        justify-content: center;
        width: 100%;

        a {
          padding: .25rem .5rem;
        }
      }
    }
  }

  &__left_menu {
    align-items: stretch;
    display: flex;
    gap: helpers.px-to-rem(50);
    justify-content: left;
  }

  &__menu-logo {
    align-items: center;
    cursor: pointer;
    display: flex;
    grid-area: logo;

    @include mq.mobileOnly {
      margin: .3rem -5px 0 7.5px !important;
    }

    img {
      display: block;
      height: 2.5rem;
      object-fit: contain;
      width: auto;

      @include mq.mobileOnly {
        height: 2.2rem;
      }
    }
  }

  &__cart-page {
    align-items: center;
    color: var(--color-black);
    display: flex;
    font-size: 2rem;
    justify-content: center;
    text-decoration: none;
    transition: color .25s;

    @include mq.mobileOnly() {
      display: none;
    }
  }

  &__right_menu {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    gap: helpers.px-to-rem(15);
    height: 100%;
    justify-content: flex-end;
    
    .header-area__favorites {
      margin-right: helpers.px-to-rem(-5);
    }

    @include mq.mobileOnly() {
      gap: 0;
    }

    @media (max-width: 1522px) {
      max-width: 370px;
    }
  }

  &__item {
    color: var(--header-font-color);
    text-decoration: none;
    transition: color .25s;

    @include mq.mq(variables.$mq-4-max) {
      padding: 0;
    }

    &:hover {
      color: var(--color-orange);
      cursor: pointer;
    }
  }

  &__main-menu-background {
    display: none;

    #{$root}--menu-open & {
      display: block;
      background-color: variables.$colorWhite;
      height: calc(100vh - var(--menu-height));
      left: 0;
      position: absolute;
      top: var(--menu-height);
      width: 100%;
    }

    @include mq.mq(variables.$mq-5-min) {
      display: none !important;
    }
  }

  &__user-logged-in {
    align-items: center;
    display: flex;
    gap: helpers.px-to-rem(15);
    height: 100%;
    justify-content: center;

    @include mq.mq(variables.$mq-4-max) {
      flex-direction: column;
      width: 100%;
    }
  }

  &__favorites {
    display: inline-flex;
    align-items: center;
    color: black;
    text-decoration: none;
    transition: color .25s;
    
    &:hover {
      color: var(--color-orange);
      cursor: pointer;
    }
    &-icon {
      width: 1.3rem;
      height: 1.3rem;
      transition: opacity 0.2s ease;

      @include mq.mq(variables.$mq-1-max) {
        width: 1.25rem;
        height: 1.25rem;
      }
    }
  }
}
</style>